/**
 * Game Data Processor
 * Handles transformation and processing of game data from APIs
 */

import {
  GAME_LIMITS,
  RANDOM_CONFIG,
  DEFAULTS,
  VALIDATION
} from '@/constants/gameConstants'

export class GameDataProcessor {
  /**
   * Transform API game data to internal format
   * Handles both old format (game_id, game_name, logo_v) and new format (id, gameName, image)
   * @param {Object} apiGame - Raw game data from API
   * @returns {Object} Transformed game object
   */
  static transformGameData(apiGame) {
    // Handle new API format (already has correct field names)
    if (apiGame.id && apiGame.gameName) {
      return {
        id: apiGame.id,
        gameName: apiGame.gameName,
        provider: apiGame.provider,
        gameCategory: apiGame.gameCategory || DEFAULTS.GAME_CATEGORY,
        gameType: apiGame.gameType || [DEFAULTS.GAME_CATEGORY],
        status: apiGame.status || DEFAULTS.GAME_STATUS,
        image: apiGame.image || { vertical: '', horizontal: '' },
        rtp: Number(apiGame.rtp) || 0,
        online: Number(apiGame.online) || 0,
        percentage: Number(apiGame.rtp) || 0
      }
    }

    // Handle old API format (game_id, game_name, logo_v)
    return {
      id: apiGame.game_id,
      gameName: apiGame.game_name,
      provider: apiGame.provider,
      gameCategory: DEFAULTS.GAME_CATEGORY,
      gameType: [DEFAULTS.GAME_CATEGORY],
      status: DEFAULTS.GAME_STATUS,
      image: {
        vertical: apiGame.logo_v || '',
        horizontal: apiGame.logo_h || apiGame.logo_v || ''
      },
      rtp: Number(apiGame.rtp) || 0,
      online: Number(apiGame.online) || 0,
      percentage: Number(apiGame.rtp) || 0
    }
  }

  /**
   * Generate mock online count for games
   * @returns {number} Random online count
   */
  static generateMockOnlineCount() {
    return (
      Math.floor(
        Math.random() *
          (DEFAULTS.ONLINE_COUNT_MAX - DEFAULTS.ONLINE_COUNT_MIN + 1)
      ) + DEFAULTS.ONLINE_COUNT_MIN
    )
  }

  /**
   * Process new games data for homepage and menu
   * @param {Array} gameArray - Array of game objects
   * @returns {Object} Processed new games data
   */
  static processNewGames(gameArray) {
    const processed = gameArray.map(game =>
      GameDataProcessor.transformGameData(game)
    )

    return {
      unlimited: processed,
      limited: processed.slice(0, GAME_LIMITS.HOMEPAGE_DISPLAY)
    }
  }

  /**
   * Process bonus games with random selections
   * @param {Array} gameArray - Array of game objects
   * @returns {Object} Processed bonus games data
   */
  static processBonusGames(gameArray) {
    const processed = gameArray.map(game =>
      GameDataProcessor.transformGameData(game)
    )

    // Validate minimum games for random selection
    if (processed.length < VALIDATION.MIN_GAMES_FOR_RANDOM) {
      console.warn(
        `Not enough games for random selection. Required: ${VALIDATION.MIN_GAMES_FOR_RANDOM}, Got: ${processed.length}`
      )
      return {
        unlimited: processed,
        bonusGames: processed.slice(0, GAME_LIMITS.BONUS_GAMES_RANDOM),
        newUserGames: processed.slice(0, GAME_LIMITS.NEW_USER_GAMES_RANDOM)
      }
    }

    // Create randomized selections
    const shuffledGames = GameDataProcessor.shuffleArray([...processed])

    return {
      unlimited: processed,
      bonusGames: shuffledGames.slice(
        RANDOM_CONFIG.BONUS_GAMES_START_INDEX,
        RANDOM_CONFIG.BONUS_GAMES_END_INDEX
      ),
      newUserGames: shuffledGames.slice(
        RANDOM_CONFIG.NEW_USER_GAMES_START_INDEX,
        RANDOM_CONFIG.NEW_USER_GAMES_END_INDEX
      )
    }
  }

  /**
   * Process standard game array (like hotPGGames)
   * @param {Array} gameArray - Array of game objects
   * @returns {Array} Processed game array
   */
  static processStandardGames(gameArray) {
    return gameArray.map(game => GameDataProcessor.transformGameData(game))
  }

  /**
   * Shuffle array using Fisher-Yates algorithm
   * @param {Array} array - Array to shuffle
   * @returns {Array} Shuffled array
   */
  static shuffleArray(array) {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }

  /**
   * Process API response and return transformed data
   * Handles both old format (data.result) and new format (direct data)
   * @param {Object} apiResponse - Raw API response
   * @returns {Object} Processed homepage games
   */
  static processApiResponse(apiResponse) {
    // Handle new API format: { success: true, newGames: [...], popularGames: [...], ... }
    // Handle old API format: { success: true, data: { result: { ... } } }
    let result

    if (apiResponse?.data?.result) {
      // Old format
      result = apiResponse.data.result
    } else if (apiResponse?.success && apiResponse?.newGames) {
      // New format - data is directly in the response
      result = apiResponse
    } else if (apiResponse?.data?.newGames) {
      // Alternative new format - data is in apiResponse.data
      result = apiResponse.data
    } else {
      throw new Error('Invalid API response format')
    }

    const processed = {}

    // List of game array keys to process
    const gameKeys = ['newGames', 'bonusGames', 'popularGames', 'hotPGGames']

    gameKeys.forEach(key => {
      if (!Array.isArray(result[key])) {
        return
      }

      switch (key) {
        case 'newGames': {
          const newGamesData = GameDataProcessor.processNewGames(result[key])
          processed.newGames = newGamesData.limited
          processed.newGamesUnlimited = newGamesData.unlimited
          break
        }
        case 'bonusGames': {
          const bonusGamesData = GameDataProcessor.processBonusGames(
            result[key]
          )
          processed.bonusGames = bonusGamesData.bonusGames
          processed.newUserGames = bonusGamesData.newUserGames
          processed.bonusGamesUnlimited = bonusGamesData.unlimited
          break
        }
        case 'popularGames': {
          const popularGamesData = GameDataProcessor.processNewGames(
            result[key]
          )
          processed.popularGames = popularGamesData.limited
          processed.popularGamesUnlimited = popularGamesData.unlimited
          break
        }
        default:
          processed[key] = GameDataProcessor.processStandardGames(result[key])
          break
      }
    })

    // Ensure all required fields exist
    const requiredFields = [
      'bonusGames',
      'newGames',
      'newUserGames',
      'hotPGGames',
      'popularGames'
    ]
    requiredFields.forEach(field => {
      if (!processed[field]) {
        processed[field] = []
      }
    })

    return processed
  }

  /**
   * Get cache data for specific game type
   * @param {Object} processedData - Processed game data
   * @param {string} cacheKey - Cache key to extract data for
   * @returns {Array} Game data for cache key
   */
  static getCacheData(processedData, cacheKey) {
    const cacheDataMap = {
      'new-games': processedData.newGamesUnlimited || [],
      'hot-games': processedData.bonusGamesUnlimited || [],
      'popular-games': processedData.popularGamesUnlimited || [],
      bonusGames: processedData.bonusGames || [],
      newGames: processedData.newGames || [],
      newUserGames: processedData.newUserGames || [],
      hotPGGames: processedData.hotPGGames || [],
      popularGames: processedData.popularGames || []
    }

    return cacheDataMap[cacheKey] || []
  }
}

export default GameDataProcessor
